import axios, { AxiosInstance, AxiosResponse, AxiosError } from "axios";
import { tokenUsageTracker } from "./tokenUsageTracker";
import { taskWorkerApi } from "./api";
import { formatMealPlanNames } from "../utils/mealPlanFormatting";
import { shouldSkipRefine } from "../utils";
import {
    OutgoingSearchResults,
    LLMMealPlanResultOutgoing,
    MealPlanPreview,
    InstacartResponse,
    ShoppingItem,
    PassioFoodDetailResponse,
    IngredientExtract,
} from "../types";

export interface PassioApiClientConfig {
    apiKey: string;
    baseUrl?: string;
    timeout?: number;
    defaultCacheTtl?: number; // Default TTL in minutes
}

export interface TokenResponse {
    access_token: string;
    token_type?: string;
    expires_in?: number;
}

export interface SearchFoodOptions {
    metadata?: string; // JSON-encoded and base64-URL encoded key/value pairs
    smartSearch?: boolean;
}

export interface MealPlanGenerationOptions {
    constraints?: Record<string, any>;
    // Add other meal plan specific options as needed
}

export interface RegenerateMealsOptions {
    mealPlan: any; // The complete meal plan data
    specificMeals?: string[]; // Array of meal identifiers to regenerate
    userFeedback?: string; // Optional feedback about what they didn't like
    constraints?: Record<string, any>;
    // Add other regeneration specific options as needed
}

export interface MealPlanQueryParams {
    model?: string;
    model2?: string;
    [key: string]: any; // Allow additional query parameters
}

export interface TaskSubmissionOptions {
    useTaskWorker?: boolean;
    userId?: number;
    profileId?: number | null;
}

export interface CacheOptions {
    ttl?: number; // TTL in minutes, undefined means no caching
    cacheKey?: string; // Custom cache key, if not provided will be auto-generated
}

interface CacheEntry<T> {
    data: T;
    timestamp: number;
    ttl: number; // TTL in milliseconds
}

class ApiCache {
    private cache = new Map<string, CacheEntry<any>>();

    set<T>(key: string, data: T, ttlMinutes: number): void {
        const ttlMs = ttlMinutes * 60 * 1000; // Convert minutes to milliseconds
        this.cache.set(key, {
            data,
            timestamp: Date.now(),
            ttl: ttlMs,
        });
        console.debug("💾 Cache SET:", key, `(TTL: ${ttlMinutes}min)`);
    }

    get<T>(key: string): T | null {
        const entry = this.cache.get(key);
        if (!entry) {
            console.debug("🔍 Cache MISS:", key);
            return null;
        }

        const now = Date.now();
        if (now - entry.timestamp > entry.ttl) {
            // Entry has expired
            console.debug("⏰ Cache EXPIRED:", key);
            this.cache.delete(key);
            return null;
        }

        console.debug("✅ Cache HIT:", key);
        return entry.data as T;
    }

    clear(): void {
        this.cache.clear();
    }

    delete(key: string): boolean {
        return this.cache.delete(key);
    }

    // Clean up expired entries
    cleanup(): void {
        const now = Date.now();
        this.cache.forEach((entry, key) => {
            if (now - entry.timestamp > entry.ttl) {
                this.cache.delete(key);
            }
        });
    }
}

export class PassioApiClient {
    private apiKey: string;
    private baseUrl: string;
    private accessToken: string | null = null;
    private tokenExpiresAt: number | null = null; // Timestamp when token expires
    private axiosInstance: AxiosInstance;
    private tokenRefreshPromise: Promise<string> | null = null;
    private cache: ApiCache;
    private defaultCacheTtl: number; // in minutes
    private cleanupInterval: NodeJS.Timeout | null = null;

    constructor(config: PassioApiClientConfig) {
        this.apiKey = config.apiKey;
        this.baseUrl = config.baseUrl || "https://api-dev.passiolife.com";
        this.defaultCacheTtl = config.defaultCacheTtl || 10; // Default 10 minutes
        this.cache = new ApiCache();

        // Setup periodic cache cleanup (every 5 minutes)
        this.cleanupInterval = setInterval(() => {
            this.cache.cleanup();
        }, 5 * 60 * 1000);

        // Create axios instance with default config
        this.axiosInstance = axios.create({
            baseURL: this.baseUrl,
            timeout: config.timeout || 30000,
            headers: {
                "Content-Type": "application/json",
            },
        });

        // Add request interceptor to add auth header
        this.axiosInstance.interceptors.request.use((config) => {
            if (this.accessToken) {
                config.headers.Authorization = `Bearer ${this.accessToken}`;
            }
            return config;
        });

        // Add response interceptor to handle 403 errors
        this.axiosInstance.interceptors.response.use(
            (response) => response,
            async (error: AxiosError) => {
                const originalRequest = error.config as any;

                if (error.response?.status === 403 && !originalRequest._retry) {
                    // Mark this request as retried to prevent infinite loops
                    originalRequest._retry = true;

                    try {
                        // Force token refresh
                        this.accessToken = null;
                        this.tokenExpiresAt = null;
                        await this.refreshToken();

                        // Retry the original request with new token
                        if (originalRequest) {
                            originalRequest.headers.Authorization = `Bearer ${this.accessToken}`;
                            return this.axiosInstance.request(originalRequest);
                        }
                    } catch (refreshError) {
                        // If refresh fails, throw the original error
                        console.error("Token refresh failed:", refreshError);
                    }
                }
                throw error;
            }
        );
    }

    // Cleanup method to clear intervals
    destroy(): void {
        if (this.cleanupInterval) {
            clearInterval(this.cleanupInterval);
            this.cleanupInterval = null;
        }
        this.cache.clear();
    }

    // Generate cache key for requests with case-insensitive text normalization
    private generateCacheKey(
        method: string,
        endpoint: string,
        data?: any,
        params?: any
    ): string {
        const keyParts = [method, endpoint];

        // Normalize data for case-insensitive caching
        if (data) {
            const normalizedData = this.normalizeForCache(data);
            keyParts.push(JSON.stringify(normalizedData));
        }

        // Normalize params for case-insensitive caching
        if (params) {
            const normalizedParams = this.normalizeForCache(params);
            keyParts.push(JSON.stringify(normalizedParams));
        }

        return keyParts.join("|");
    }

    // Normalize text fields in objects for case-insensitive caching
    private normalizeForCache(obj: any): any {
        if (obj === null || obj === undefined) {
            return obj;
        }

        if (typeof obj === "string") {
            return obj.toLowerCase().trim();
        }

        if (Array.isArray(obj)) {
            return obj.map((item) => this.normalizeForCache(item));
        }

        if (typeof obj === "object") {
            const normalized: any = {};
            for (const [key, value] of Object.entries(obj)) {
                // Normalize text-based search fields
                if (key === "term" || key === "input" || key === "textInput") {
                    normalized[key] =
                        typeof value === "string"
                            ? value.toLowerCase().trim()
                            : value;
                } else {
                    normalized[key] = this.normalizeForCache(value);
                }
            }
            return normalized;
        }

        return obj;
    }

    /**
     * Get authentication token from Passio API
     */
    private async getToken(): Promise<string> {
        try {
            const response: AxiosResponse<TokenResponse> = await axios.get(
                `${this.baseUrl}/v2/token-cache/unified/oauth/token/${this.apiKey}`
            );

            if (!response.data.access_token) {
                throw new Error("No access token received from API");
            }

            this.accessToken = response.data.access_token;

            // Calculate token expiry time (subtract 5 minutes for safety margin)
            if (response.data.expires_in) {
                const expiresInMs = (response.data.expires_in - 300) * 1000; // 5 minute buffer
                this.tokenExpiresAt = Date.now() + expiresInMs;
            } else {
                // Default to 23 hours if no expiry provided
                this.tokenExpiresAt = Date.now() + 23 * 60 * 60 * 1000;
            }

            return this.accessToken;
        } catch (error) {
            console.error("Failed to get auth token:", error);
            throw new Error("Authentication failed");
        }
    }

    /**
     * Check if token is expired or about to expire
     */
    private isTokenExpired(): boolean {
        if (!this.accessToken || !this.tokenExpiresAt) {
            return true;
        }
        // Check if token expires in the next 5 minutes
        return Date.now() >= this.tokenExpiresAt;
    }

    /**
     * Refresh the authentication token
     */
    private async refreshToken(): Promise<string> {
        // Prevent multiple simultaneous token refresh requests
        if (this.tokenRefreshPromise) {
            return this.tokenRefreshPromise;
        }

        this.tokenRefreshPromise = this.getToken();

        try {
            const token = await this.tokenRefreshPromise;
            return token;
        } finally {
            this.tokenRefreshPromise = null;
        }
    }

    /**
     * Ensure we have a valid authentication token
     */
    private async ensureAuthenticated(): Promise<void> {
        if (!this.accessToken || this.isTokenExpired()) {
            await this.refreshToken();
        }
    }

    /**
     * Make an authenticated API request with optional caching
     */
    private async makeRequest<T>(
        method: "GET" | "POST" | "PUT" | "DELETE",
        endpoint: string,
        data?: any,
        params?: Record<string, any>,
        cacheOptions?: CacheOptions,
        timeoutMs?: number
    ): Promise<T> {
        // Check cache first if caching is enabled
        let cacheKey: string | null = null;
        if (cacheOptions?.ttl !== undefined && cacheOptions.ttl > 0) {
            cacheKey =
                cacheOptions.cacheKey ||
                this.generateCacheKey(method, endpoint, data, params);
            const cachedData = this.cache.get<T>(cacheKey);
            if (cachedData !== null) {
                return cachedData;
            }
        }

        await this.ensureAuthenticated();

        const requestConfig: any = {
            method,
            url: endpoint,
            data,
            params,
        };

        // Override timeout if specified
        if (timeoutMs !== undefined) {
            requestConfig.timeout = timeoutMs;
        }

        const response: AxiosResponse<T> = await this.axiosInstance.request(
            requestConfig
        );

        // Update token usage from response headers (except for auth endpoints)
        if (
            !endpoint.includes("/token-cache/") &&
            !endpoint.includes("/oauth/token")
        ) {
            const headers: Record<string, string> = {};
            Object.keys(response.headers).forEach((key) => {
                headers[key.toLowerCase()] = response.headers[key];
            });
            tokenUsageTracker.updateUsageFromHeaders(headers);
        }

        // Cache the response if caching is enabled
        if (
            cacheKey &&
            cacheOptions?.ttl !== undefined &&
            cacheOptions.ttl > 0
        ) {
            this.cache.set(cacheKey, response.data, cacheOptions.ttl);
        }

        return response.data;
    }

    // API Methods

    /**
     * Search for food items (Basic Search)
     * Searches for food (generic, branded or restaurant) or recipes
     */
    async searchFoodsBasic(
        term: string,
        options: SearchFoodOptions = {},
        cacheOptions?: CacheOptions
    ): Promise<OutgoingSearchResults> {
        const params: Record<string, any> = {
            term,
        };

        // Add optional parameters if provided
        if (options.metadata !== undefined) {
            params.metadata = options.metadata;
        }
        if (options.smartSearch !== undefined) {
            params.smartSearch = options.smartSearch;
        }

        const finalCacheOptions = {
            ttl: cacheOptions?.ttl ?? this.defaultCacheTtl,
            cacheKey: cacheOptions?.cacheKey,
        };

        return this.makeRequest<OutgoingSearchResults>(
            "GET",
            "/v2/products/napi/food/search/advanced",
            undefined,
            params,
            finalCacheOptions
        );
    }

    /**
     * Search for food items (Semantic Search)
     * Searches for food (generic, branded or restaurant) or recipes using semantic search
     */
    async searchFoodsSemantic(
        term: string,
        options: SearchFoodOptions = {},
        cacheOptions?: CacheOptions
    ): Promise<OutgoingSearchResults> {
        const params: Record<string, any> = {
            term,
        };

        // Add optional parameters if provided
        if (options.metadata !== undefined) {
            params.metadata = options.metadata;
        }
        if (options.smartSearch !== undefined) {
            params.smartSearch = options.smartSearch;
        }

        const finalCacheOptions = {
            ttl: cacheOptions?.ttl ?? this.defaultCacheTtl,
            cacheKey: cacheOptions?.cacheKey,
        };

        return this.makeRequest<OutgoingSearchResults>(
            "GET",
            "/v2/products/napi/food/search/semantic",
            undefined,
            params,
            finalCacheOptions
        );
    }

    /**
     * Legacy method - kept for backward compatibility
     * @deprecated Use searchFoodsBasic instead
     */
    async searchFood(
        term: string,
        options?: {
            offset?: number;
            [key: string]: any;
        },
        cacheOptions?: CacheOptions
    ): Promise<any> {
        const params = {
            term,
            ...options,
        };

        const finalCacheOptions = {
            ttl: cacheOptions?.ttl ?? this.defaultCacheTtl,
            cacheKey: cacheOptions?.cacheKey,
        };

        return this.makeRequest(
            "GET",
            "/v2/products/napi/food/search/advanced",
            undefined,
            params,
            finalCacheOptions
        );
    }

    /**
     * Get food item details by ID
     */
    async getFoodById(id: string, cacheOptions?: CacheOptions): Promise<any> {
        const finalCacheOptions = {
            ttl: cacheOptions?.ttl ?? this.defaultCacheTtl,
            cacheKey: cacheOptions?.cacheKey,
        };

        return this.makeRequest(
            "GET",
            `/v2/products/napi/food/${id}`,
            undefined,
            undefined,
            finalCacheOptions
        );
    }

    /**
     * Get food item details by refCode
     * Returns detailed PassioFood information including available portions
     */
    async getFoodByRefCode(
        refCode: string,
        cacheOptions?: CacheOptions
    ): Promise<PassioFoodDetailResponse> {
        const finalCacheOptions = {
            ttl: cacheOptions?.ttl ?? this.defaultCacheTtl,
            cacheKey: cacheOptions?.cacheKey,
        };

        return this.makeRequest<PassioFoodDetailResponse>(
            "GET",
            `/v2/products/napi/food/search/result/refCode/${refCode}`,
            undefined,
            undefined,
            finalCacheOptions
        );
    }

    /**
     * Search for recipes
     */
    async searchRecipes(
        term: string,
        options?: {
            offset?: number;
            [key: string]: any;
        },
        cacheOptions?: CacheOptions
    ): Promise<any> {
        const params = {
            term,
            ...options,
        };

        const finalCacheOptions = {
            ttl: cacheOptions?.ttl ?? this.defaultCacheTtl,
            cacheKey: cacheOptions?.cacheKey,
        };

        return this.makeRequest(
            "GET",
            "/v2/products/napi/recipe/search",
            undefined,
            params,
            finalCacheOptions
        );
    }

    /**
     * Get recipe details by ID
     */
    async getRecipeById(id: string, cacheOptions?: CacheOptions): Promise<any> {
        const finalCacheOptions = {
            ttl: cacheOptions?.ttl ?? this.defaultCacheTtl,
            cacheKey: cacheOptions?.cacheKey,
        };

        return this.makeRequest(
            "GET",
            `/v2/products/napi/recipe/${id}`,
            undefined,
            undefined,
            finalCacheOptions
        );
    }

    /**
     * Generate Meal Plan
     * Note: Meal plan generation is typically not cached by default as it's dynamic content
     */
    async generateMealPlan(
        textInput: string,
        options: MealPlanGenerationOptions = {},
        queryParams?: MealPlanQueryParams,
        cacheOptions?: CacheOptions
    ): Promise<LLMMealPlanResultOutgoing> {
        await this.ensureAuthenticated();

        const requestData = {
            content: textInput,
            ...options,
        };

        // Check if skipRefine should be added based on browser URL
        const finalQueryParams = { ...queryParams };
        if (shouldSkipRefine()) {
            finalQueryParams.skipRefine = true;
        }

        // Default to no caching for dynamic content unless explicitly requested
        const finalCacheOptions = cacheOptions
            ? {
                  ttl: cacheOptions.ttl ?? 0, // Default to no cache for meal plans
                  cacheKey: cacheOptions.cacheKey,
              }
            : undefined;

        // Use 10-minute timeout for meal plan generation (600,000ms)
        const result = await this.makeRequest<LLMMealPlanResultOutgoing>(
            "POST",
            "/v2/products/sdk/tools/generateMealPlan",
            requestData,
            finalQueryParams,
            finalCacheOptions,
            600000 // 10 minutes
        );

        // Format all meal plan names to ensure consistent title casing
        return formatMealPlanNames(result);
    }

    /**
     * Generate Meal Plan Preview
     * Note: Meal plan preview generation is typically not cached by default as it's dynamic content
     */
    async generateMealPlanPreview(
        textInput: string,
        options: MealPlanGenerationOptions = {},
        queryParams?: MealPlanQueryParams,
        cacheOptions?: CacheOptions
    ): Promise<MealPlanPreview> {
        await this.ensureAuthenticated();

        const requestData = {
            content: textInput,
            ...options,
        };

        // Check if skipRefine should be added based on browser URL
        const finalQueryParams = { ...queryParams };
        if (shouldSkipRefine()) {
            finalQueryParams.skipRefine = true;
        }

        // Default to no caching for dynamic content unless explicitly requested
        const finalCacheOptions = cacheOptions
            ? {
                  ttl: cacheOptions.ttl ?? 0, // Default to no cache for meal plan previews
                  cacheKey: cacheOptions.cacheKey,
              }
            : undefined;

        // Use 10-minute timeout for meal plan preview generation (600,000ms)
        return this.makeRequest<MealPlanPreview>(
            "POST",
            "/v2/products/sdk/tools/generateMealPlanPreview",
            requestData,
            finalQueryParams,
            finalCacheOptions,
            600000 // 10 minutes
        );
    }

    /**
     * Convert Meal Plan to Affiliate Link
     * Converts a meal plan to an affiliate link for shopping platforms (e.g., Instacart)
     */
    async convertMealPlanToAffiliateLink(
        mealPlan: LLMMealPlanResultOutgoing,
        cacheOptions?: CacheOptions
    ): Promise<InstacartResponse>;
    async convertMealPlanToAffiliateLink(
        shoppingList: ShoppingItem[],
        cacheOptions?: CacheOptions
    ): Promise<InstacartResponse>;
    async convertMealPlanToAffiliateLink(
        input: LLMMealPlanResultOutgoing | ShoppingItem[],
        cacheOptions?: CacheOptions
    ): Promise<InstacartResponse> {
        await this.ensureAuthenticated();

        // Default to no caching for affiliate links as they might be user-specific
        const finalCacheOptions = cacheOptions
            ? {
                  ttl: cacheOptions.ttl ?? 0,
                  cacheKey: cacheOptions.cacheKey,
              }
            : undefined;

        return this.makeRequest<InstacartResponse>(
            "POST",
            "/v2/products/sdk/affiliates/convertMealPlanToAffiliateLink",
            input,
            undefined,
            finalCacheOptions
        );
    }

    /**
     * Generic method for custom API calls
     */
    async get<T>(
        endpoint: string,
        params?: Record<string, any>,
        cacheOptions?: CacheOptions
    ): Promise<T> {
        const finalCacheOptions = cacheOptions
            ? {
                  ttl: cacheOptions.ttl ?? this.defaultCacheTtl,
                  cacheKey: cacheOptions.cacheKey,
              }
            : { ttl: this.defaultCacheTtl };

        return this.makeRequest<T>(
            "GET",
            endpoint,
            undefined,
            params,
            finalCacheOptions
        );
    }

    async post<T>(
        endpoint: string,
        data?: any,
        params?: Record<string, any>,
        cacheOptions?: CacheOptions
    ): Promise<T> {
        // Default to no caching for POST requests unless explicitly requested
        const finalCacheOptions = cacheOptions
            ? {
                  ttl: cacheOptions.ttl ?? 0,
                  cacheKey: cacheOptions.cacheKey,
              }
            : undefined;

        return this.makeRequest<T>(
            "POST",
            endpoint,
            data,
            params,
            finalCacheOptions
        );
    }

    async put<T>(
        endpoint: string,
        data?: any,
        params?: Record<string, any>,
        cacheOptions?: CacheOptions
    ): Promise<T> {
        // Default to no caching for PUT requests unless explicitly requested
        const finalCacheOptions = cacheOptions
            ? {
                  ttl: cacheOptions.ttl ?? 0,
                  cacheKey: cacheOptions.cacheKey,
              }
            : undefined;

        return this.makeRequest<T>(
            "PUT",
            endpoint,
            data,
            params,
            finalCacheOptions
        );
    }

    async delete<T>(
        endpoint: string,
        params?: Record<string, any>,
        cacheOptions?: CacheOptions
    ): Promise<T> {
        // Default to no caching for DELETE requests unless explicitly requested
        const finalCacheOptions = cacheOptions
            ? {
                  ttl: cacheOptions.ttl ?? 0,
                  cacheKey: cacheOptions.cacheKey,
              }
            : undefined;

        return this.makeRequest<T>(
            "DELETE",
            endpoint,
            undefined,
            params,
            finalCacheOptions
        );
    }

    // Cache management methods
    /**
     * Clear all cached data
     */
    clearCache(): void {
        this.cache.clear();
    }

    /**
     * Delete specific cache entry
     */
    deleteCacheEntry(key: string): boolean {
        return this.cache.delete(key);
    }

    /**
     * Manually trigger cache cleanup
     */
    cleanupCache(): void {
        this.cache.cleanup();
    }

    /**
     * Check if client is authenticated
     */
    isAuthenticated(): boolean {
        return !!this.accessToken;
    }

    /**
     * Clear authentication token
     */
    clearAuth(): void {
        this.accessToken = null;
        this.tokenExpiresAt = null;
    }

    /**
     * Update API key and clear existing authentication
     */
    updateApiKey(newApiKey: string): void {
        this.apiKey = newApiKey;
        this.clearAuth();
    }

    /**
     * Update base URL
     */
    updateBaseUrl(newBaseUrl: string): void {
        this.baseUrl = newBaseUrl;
        this.axiosInstance.defaults.baseURL = newBaseUrl;
        this.clearAuth(); // Clear auth as it might be different for different environments
    }

    /**
     * Initialize token usage tracking by making a simple API call
     */
    async initializeTokenUsage(): Promise<void> {
        await tokenUsageTracker.initialize(async () => {
            // Make a simple search for "apple" to get usage headers
            await this.searchFoodsBasic("apple", {}, { ttl: 0 }); // Don't cache this initialization call
        });
    }

    /**
     * Get the current access token, ensuring it's valid
     * This method will refresh the token if needed
     */
    async getAccessToken(): Promise<string> {
        await this.ensureAuthenticated();
        if (!this.accessToken) {
            throw new Error("Failed to get access token");
        }
        return this.accessToken;
    }

    /**
     * Regenerate Meals
     * Regenerates specific meals or days in an existing meal plan
     */
    async regenerateMeals(
        options: RegenerateMealsOptions,
        queryParams?: MealPlanQueryParams,
        cacheOptions?: CacheOptions
    ): Promise<LLMMealPlanResultOutgoing> {
        await this.ensureAuthenticated();

        // Format the request data to match other meal plan endpoints
        const regenerationData = {
            mealPlan: options.mealPlan,
            specificMeals: options.specificMeals,
            userFeedback: options.userFeedback,
            constraints: options.constraints,
        };

        const requestData = {
            content: JSON.stringify(regenerationData),
        };

        // Check if skipRefine should be added based on browser URL
        const finalQueryParams = { ...queryParams };
        if (shouldSkipRefine()) {
            finalQueryParams.skipRefine = true;
        }

        // Default to no caching for dynamic content unless explicitly requested
        const finalCacheOptions = cacheOptions
            ? {
                  ttl: cacheOptions.ttl ?? 0, // Default to no cache for meal regeneration
                  cacheKey: cacheOptions.cacheKey,
              }
            : undefined;

        // Use 10-minute timeout for meal regeneration (600,000ms)
        const result = await this.makeRequest<LLMMealPlanResultOutgoing>(
            "POST",
            "/v2/products/sdk/tools/regenerateMeals",
            requestData,
            finalQueryParams,
            finalCacheOptions,
            600000 // 10 minutes
        );

        // Format all meal plan names to ensure consistent title casing
        return formatMealPlanNames(result);
    }

    /**
     * Update Meal Plan Shopping List
     * Regenerates and humanizes the shopping list for an existing meal plan
     */
    async updateMealPlanShoppingList(
        mealPlan: LLMMealPlanResultOutgoing,
        cacheOptions?: CacheOptions,
        model?: string | null
    ): Promise<LLMMealPlanResultOutgoing> {
        await this.ensureAuthenticated();

        // Default to no caching for dynamic content
        const finalCacheOptions = cacheOptions
            ? {
                  ttl: cacheOptions.ttl ?? 0,
                  cacheKey: cacheOptions.cacheKey,
              }
            : undefined;

        let defaultParam = {
            model: "gemini-2-5-flash",
        };
        if (model !== null && model) {
            defaultParam.model = model;
        }
        // Use standard timeout for shopping list update
        const result = await this.makeRequest<LLMMealPlanResultOutgoing>(
            "POST",
            "/v2/products/sdk/tools/updateMealPlanShoppingList",
            mealPlan,
            defaultParam,
            finalCacheOptions,
            60000 * 3 // 3 minute timeout
        );

        // Format all meal plan names to ensure consistent title casing
        return formatMealPlanNames(result);
    }

    /**
     * Detect Ingredients from Multiple Media Files
     * Analyzes multiple images/videos to extract all ingredients found
     * @param files Array of File objects (images or videos)
     * @param options Optional parameters like intelligenceProfile
     * @returns List of detected ingredients with portion and weight information
     */
    async detectIngredientsFromMultiMedia(
        files: File[],
        options?: {
            intelligenceProfile?: string;
            model?: string;
            [key: string]: any;
        },
        cacheOptions?: CacheOptions
    ): Promise<IngredientExtract[]> {
        await this.ensureAuthenticated();

        // Validate files
        if (!files || files.length === 0) {
            throw new Error("At least one file is required");
        }

        if (files.length > 5) {
            throw new Error("Maximum 5 files allowed per request");
        }

        // Check file sizes (50MB per file, 100MB total)
        const totalSize = files.reduce((sum, file) => sum + file.size, 0);
        const maxFileSize = 50 * 1024 * 1024; // 50MB
        const maxTotalSize = 100 * 1024 * 1024; // 100MB

        for (const file of files) {
            if (file.size > maxFileSize) {
                throw new Error(`File ${file.name} exceeds 50MB limit`);
            }
        }

        if (totalSize > maxTotalSize) {
            throw new Error("Total file size exceeds 100MB limit");
        }

        // Create FormData
        const formData = new FormData();
        
        // Add files with unique field names
        files.forEach((file, index) => {
            const fieldName = file.type.startsWith('video/') 
                ? `video${index + 1}` 
                : `image${index + 1}`;
            formData.append(fieldName, file);
        });

        // Extract model parameter for query string
        const queryParams: Record<string, any> = {};
        if (options?.model) {
            queryParams.model = options.model;
        }

        // Add optional JSON data (excluding model which goes in query params)
        if (options && Object.keys(options).length > 0) {
            const { model, ...jsonOptions } = options;
            if (Object.keys(jsonOptions).length > 0) {
                formData.append('json', JSON.stringify(jsonOptions));
            }
        }

        // Make request with FormData
        // Note: We need to use the axios instance directly for FormData
        const response = await this.axiosInstance.post<IngredientExtract[]>(
            "/v2/products/sdk/tools/vision/detectIngredientsFromMultiMedia",
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                params: queryParams,
                timeout: 300000, // 5 minutes for processing multiple media files
            }
        );

        // Update token usage from response headers
        const headers: Record<string, string> = {};
        Object.keys(response.headers).forEach((key) => {
            headers[key.toLowerCase()] = response.headers[key];
        });
        tokenUsageTracker.updateUsageFromHeaders(headers);

        // Ensure we return an array even if the response is null
        let ingredients = Array.isArray(response.data) ? response.data : [];
        
        // Title case all ingredient names and sort alphabetically
        ingredients = ingredients.map(ingredient => ({
            ...ingredient,
            ingredientName: this.toTitleCase(ingredient.ingredientName)
        })).sort((a, b) => a.ingredientName.localeCompare(b.ingredientName));
        
        return ingredients;
    }
    
    /**
     * Convert string to title case
     */
    private toTitleCase(str: string): string {
        return str.replace(/\w\S*/g, (txt) => {
            return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
        });
    }

    /**
     * Submit Meal Plan Task
     * Submits a meal plan generation request as an async task
     */
    async submitMealPlanTask(
        textInput: string,
        metadata: { profileId?: number | null; numberOfDays?: number } = {},
        params: Record<string, string> = {}
    ): Promise<{ id: string; status: string; [key: string]: any }> {
        // Ensure we have a valid access token
        const bearerToken = await this.getAccessToken();
        
        // Build the full URL with query parameters
        const baseUrl = this.baseUrl.replace("/v2/sdk/", "/v2/products/sdk/");
        const queryParams = new URLSearchParams(params);
        const fullUrl = `${baseUrl}/v2/products/sdk/tools/generateMealPlan${
            queryParams.toString() ? `?${queryParams.toString()}` : ""
        }`;

        // Prepare the request body with days included
        const requestBody: any = {
            textInput: textInput
        };
        
        // Add numberOfDays to the request body if provided
        if (metadata.numberOfDays !== undefined) {
            requestBody.numberOfDays = metadata.numberOfDays;
        }

        // Create the task submission data
        const taskData = {
            url: fullUrl,
            postBody: JSON.stringify(requestBody),
            bearerToken: bearerToken,
            profileId: metadata.profileId || null
        };

        // Submit to our backend which will create the task
        const response = await taskWorkerApi.createTaskWorkerEvent(taskData);
        
        return response;
    }

    /**
     * Submit Meal Plan Preview Task
     * Submits a meal plan preview generation request as an async task
     */
    async submitMealPlanPreviewTask(
        textInput: string,
        metadata: { profileId?: number | null } = {},
        params: Record<string, string> = {}
    ): Promise<{ id: string; status: string; [key: string]: any }> {
        // Ensure we have a valid access token
        const bearerToken = await this.getAccessToken();
        
        // Build the full URL with query parameters
        const baseUrl = this.baseUrl.replace("/v2/sdk/", "/v2/products/sdk/");
        const queryParams = new URLSearchParams(params);
        const fullUrl = `${baseUrl}/v2/products/sdk/tools/generateMealPlanPreview${
            queryParams.toString() ? `?${queryParams.toString()}` : ""
        }`;

        // Prepare the request body
        const requestBody = {
            textInput: textInput
        };

        // Create the task submission data
        const taskData = {
            url: fullUrl,
            postBody: JSON.stringify(requestBody),
            bearerToken: bearerToken,
            profileId: metadata.profileId || null
        };

        // Submit to our backend which will create the task
        const response = await taskWorkerApi.createTaskWorkerEvent(taskData);
        
        return response;
    }
}

// Export a default instance factory
export const createPassioApiClient = (
    config: PassioApiClientConfig
): PassioApiClient => {
    return new PassioApiClient(config);
};

// Export singleton pattern helper
let defaultClient: PassioApiClient | null = null;

export const getDefaultPassioApiClient = (): PassioApiClient => {
    if (!defaultClient) {
        throw new Error(
            "Default Passio API client not initialized. Call initializeDefaultPassioApiClient first."
        );
    }
    return defaultClient;
};

export const initializeDefaultPassioApiClient = (
    config: PassioApiClientConfig
): void => {
    defaultClient = new PassioApiClient(config);
};

export default PassioApiClient;
